/* eslint-disable react-refresh/only-export-components -- shared editor exports its schedule helpers alongside the component */
import { useState, type ReactNode } from 'react';
import { X, Loader2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Badge } from '@/components/atoms/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/atoms/popover';
import { useJobTypes } from '@/hooks/settings/useJobTypes';
import { useBusinessUnits } from '@/hooks/scheduler/useBusinessUnits';
import { useEquipmentCategories } from '@/hooks/equipment/useEquipment';
import { usePricebookItems } from '@/hooks/pricebook/usePricebookItems';
import { Switch } from '@/components/atoms/switch';
import { cn } from '@/lib/utils';
import {
  type PlanRecurringService,
  type PlanRecurringServiceInput,
  type RecurrenceType,
  type DurationType,
} from '@/hooks/membership-plans/usePlanRecurringServices';
import {
  formatCurrency as currency,
  type MembershipCoverageRule,
} from './support/membership-plan-shared';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const RECURRENCE_TYPES: RecurrenceType[] = ['DAILY', 'WEEKLY', 'MONTHLY', 'SEASONAL'];
const PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'];

/**
 * The config fields the editor reads/writes, shared by any row shape (plan or
 * membership). Membership rows lack `planId`, so the editor accepts this widened
 * shape rather than the full `PlanRecurringService`.
 */
export interface RecurringServiceConfig {
  label: string | null;
  jobTypeId: string;
  businessUnitId: string | null;
  invoiceTemplateItemId: string | null;
  priority: string | null;
  jobSummary: string | null;
  recurrenceType: RecurrenceType;
  recurrenceInterval: number;
  recurrenceMonths: number[];
  durationType: DurationType;
  totalVisits: number | null;
  firstVisitComplete: boolean;
  coverageId: string | null;
  attachTo: 'PROPERTY' | 'UNIT';
  scaling: 'EXTEND' | 'MULTIPLY';
  extraUnitMinutes: number;
}

function unitWord(type: RecurrenceType, n: number): string {
  const plural = n > 1;
  if (type === 'DAILY') return plural ? 'days' : 'day';
  if (type === 'WEEKLY') return plural ? 'weeks' : 'week';
  if (type === 'MONTHLY') return plural ? 'months' : 'month';
  return plural ? 'years' : 'year';
}

/** Human schedule summary, mirroring the backend/design wording. */
export function scheduleSummary(cfg: {
  recurrenceType: RecurrenceType;
  recurrenceInterval: number;
  recurrenceMonths: number[];
  durationType: DurationType;
  totalVisits: number | null;
}): string {
  const n = cfg.recurrenceInterval || 1;
  let base: string;
  if (cfg.recurrenceType === 'SEASONAL') {
    const names = cfg.recurrenceMonths
      .slice()
      .sort((a, b) => a - b)
      .map((m) => MONTHS[m - 1])
      .filter(Boolean);
    const per = names.length || 1;
    base =
      n === 1
        ? `${per}×/year · seasonal (${names.length ? names.join(', ') : 'no month'})`
        : `${per}× every ${n} years · seasonal (${names.length ? names.join(', ') : 'no month'})`;
  } else {
    base = `every ${n} ${unitWord(cfg.recurrenceType, n)}`;
  }
  // A plan the member keeps paying for keeps delivering, so there is no tail to
  // report unless a legacy row still carries a capped series.
  if (cfg.durationType === 'FIXED') {
    return `${base} · ${cfg.totalVisits ? `${cfg.totalVisits} visits` : 'fixed'}`;
  }
  return base;
}

/** Average authored benefit occurrences per year, matching backend economics. */
export function averageRecurringOccurrencesPerYear(
  cfg: {
    recurrenceType: RecurrenceType;
    recurrenceInterval: number;
    recurrenceMonths: number[];
    durationType: DurationType;
    totalVisits: number | null;
    firstVisitComplete: boolean;
  },
  termMonths = 0,
): number {
  const interval = Math.max(1, cfg.recurrenceInterval || 1);
  const recurring =
    cfg.recurrenceType === 'DAILY'
      ? 365 / interval
      : cfg.recurrenceType === 'WEEKLY'
        ? 52 / interval
        : cfg.recurrenceType === 'SEASONAL'
          ? cfg.recurrenceMonths.length / interval
          : 12 / interval;
  if (cfg.durationType !== 'FIXED' || !cfg.totalVisits) return recurring;
  const remaining = Math.max(0, cfg.totalVisits - (cfg.firstVisitComplete ? 1 : 0));
  return termMonths > 0 ? remaining * (12 / termMonths) : Math.min(recurring, remaining);
}

/**
 * Identity of the visit promise itself. Labels, summaries, invoice lines and
 * dispatch metadata do not make a second independently scheduled visit safe;
 * schedule + job type + delivery scope do.
 */
export function recurringServicePromiseKey(
  service: Partial<RecurringServiceConfig> & { jobTypeId?: string },
): string {
  const attachTo = service.attachTo === 'UNIT' ? 'UNIT' : 'PROPERTY';
  const scaling = attachTo === 'UNIT' && service.scaling === 'MULTIPLY' ? 'MULTIPLY' : 'EXTEND';
  return JSON.stringify([
    service.jobTypeId ?? '',
    service.recurrenceType ?? 'MONTHLY',
    Math.max(1, service.recurrenceInterval ?? 1),
    [...(service.recurrenceMonths ?? [])].sort((a, b) => a - b),
    service.durationType ?? 'CONTINUOUS',
    service.durationType === 'FIXED' ? (service.totalVisits ?? null) : null,
    service.firstVisitComplete === true,
    attachTo,
    attachTo === 'UNIT' ? (service.coverageId ?? null) : null,
    scaling,
    attachTo === 'UNIT' && scaling === 'EXTEND' ? (service.extraUnitMinutes ?? 0) : 0,
  ]);
}

interface DraftState {
  label: string;
  jobTypeId: string;
  businessUnitId: string | null;
  invoiceTemplateItemId: string | null;
  priority: string | null;
  jobSummary: string;
  recurrenceType: RecurrenceType;
  recurrenceInterval: string;
  recurrenceMonths: number[];
  durationType: DurationType;
  totalVisits: string;
  firstVisitComplete: boolean;
  coverageId: string | null;
  attachTo: 'PROPERTY' | 'UNIT';
  scaling: 'EXTEND' | 'MULTIPLY';
  extraUnitMinutes: string;
}

function draftFrom(svc?: Partial<RecurringServiceConfig> | null): DraftState {
  return {
    label: svc?.label ?? '',
    jobTypeId: svc?.jobTypeId ?? '',
    businessUnitId: svc?.businessUnitId ?? null,
    invoiceTemplateItemId: svc?.invoiceTemplateItemId ?? null,
    priority: svc?.priority ?? null,
    jobSummary: svc?.jobSummary ?? '',
    recurrenceType: svc?.recurrenceType ?? 'MONTHLY',
    recurrenceInterval: String(svc?.recurrenceInterval ?? 1),
    recurrenceMonths: svc?.recurrenceMonths ?? [],
    durationType: svc?.durationType ?? 'CONTINUOUS',
    totalVisits: svc?.totalVisits != null ? String(svc.totalVisits) : '',
    firstVisitComplete: svc?.firstVisitComplete ?? false,
    coverageId: svc?.coverageId ?? null,
    attachTo: svc?.attachTo ?? 'PROPERTY',
    scaling: svc?.scaling ?? 'EXTEND',
    extraUnitMinutes: String(svc?.extraUnitMinutes ?? 0),
  };
}

/** Materialize a client-side draft row from an editor input (no server id yet). */
export function inputToRow(
  input: PlanRecurringServiceInput,
): Omit<PlanRecurringService, 'id' | 'planId'> {
  return {
    label: input.label ?? null,
    jobTypeId: input.jobTypeId,
    businessUnitId: input.businessUnitId ?? null,
    invoiceTemplateItemId: input.invoiceTemplateItemId ?? null,
    priority: input.priority ?? null,
    jobSummary: input.jobSummary ?? null,
    recurrenceType: input.recurrenceType,
    recurrenceInterval: input.recurrenceInterval ?? 1,
    recurrenceMonths: input.recurrenceMonths ?? [],
    durationType: input.durationType,
    totalVisits: input.totalVisits ?? null,
    firstVisitComplete: input.firstVisitComplete ?? false,
    coverageId: input.coverageId ?? null,
    attachTo: input.attachTo ?? 'PROPERTY',
    scaling: input.scaling ?? 'EXTEND',
    extraUnitMinutes: input.extraUnitMinutes ?? 0,
  };
}

function toInput(d: DraftState): PlanRecurringServiceInput {
  const fixed = d.durationType === 'FIXED';
  return {
    label: d.label.trim() || null,
    jobTypeId: d.jobTypeId,
    businessUnitId: d.businessUnitId || null,
    invoiceTemplateItemId: d.invoiceTemplateItemId || null,
    priority: d.priority || null,
    jobSummary: d.jobSummary.trim() || null,
    recurrenceType: d.recurrenceType,
    recurrenceInterval: Math.max(1, Number(d.recurrenceInterval) || 1),
    recurrenceMonths: d.recurrenceType === 'SEASONAL' ? d.recurrenceMonths : [],
    durationType: d.durationType,
    totalVisits: fixed ? Number(d.totalVisits) || null : null,
    firstVisitComplete: d.firstVisitComplete,
    coverageId: d.attachTo === 'UNIT' ? d.coverageId : null,
    attachTo: d.attachTo,
    scaling: d.attachTo === 'UNIT' ? d.scaling : 'EXTEND',
    extraUnitMinutes:
      d.attachTo === 'UNIT' && d.scaling === 'EXTEND'
        ? Math.max(0, Number(d.extraUnitMinutes) || 0)
        : 0,
  };
}

function Pill({
  active,
  onClick,
  disabled = false,
  children,
}: {
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={cn(
        'inline-flex h-8 items-center rounded-[7px] border px-3 text-[12px] font-medium transition-colors',
        active
          ? 'border-[#0a0a0a] bg-[#0a0a0a] text-white'
          : 'border-[#d9d6ce] bg-white text-[#6f6b64] hover:bg-[#f5f4f1]',
        disabled && 'cursor-not-allowed opacity-45 hover:bg-white',
      )}
    >
      {children}
    </button>
  );
}

/**
 * Pricebook-item autocomplete that keeps the plain `Input` look of the editor's
 * other fields — no chip. Types to search, picks to store a real line-item UUID
 * (the column is `@IsUuid`, so a hand-typed id would 400). Resolves the stored
 * id back to a name when an existing service is re-opened.
 */
function InvoiceItemPicker({
  value,
  onChange,
  invalid,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  invalid?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  // Name of the currently-linked item, so a locked pick shows a name not a uuid.
  const { data: linked } = usePricebookItems(
    { ids: value ? [value] : [] },
    { enabled: Boolean(value), showErrorToast: false },
  );
  const linkedItem = linked?.items?.[0];
  // The price is not decoration: it is what one extra covered unit is worth, so
  // the plan's extra-unit suggestion is only as honest as this pick.
  const linkedName = linkedItem
    ? `${linkedItem.name}${linkedItem.price ? ` · ${currency(linkedItem.price)}` : ''}`
    : '';

  const searchEnabled = open && query.trim().length >= 2;
  // The list endpoint filters by one type at a time, and a visit can deliver a
  // service or a bundle of them — so ask for a wider page and keep both.
  const { data, isFetching } = usePricebookItems(
    { search: query.trim(), limit: 25 },
    { enabled: searchEnabled, showErrorToast: false },
  );
  const items = (data?.items ?? [])
    .filter((item) => item.type === 'service' || item.type === 'bundle')
    .slice(0, 8);

  // Locked state: an item is chosen — read-only, no free text; the X clears it
  // back to a search field.
  if (value) {
    return (
      <div className="relative">
        <Input
          readOnly
          aria-label="Recurring service invoice item"
          value={linkedName || '…'}
          className="cursor-default pr-9"
        />
        <button
          type="button"
          onClick={() => {
            onChange(null);
            setQuery('');
            setOpen(false);
          }}
          aria-label="Change item"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <Popover open={searchEnabled} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <Input
          aria-label="Recurring service invoice item"
          value={query}
          placeholder="Search a service or bundle"
          autoComplete="off"
          className={cn(invalid && 'border-destructive ring-2 ring-destructive/20')}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
        />
      </PopoverAnchor>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-[var(--radix-popover-trigger-width)] p-1"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {isFetching ? (
          <div className="flex items-center gap-2 px-2 py-1.5 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" /> Searching…
          </div>
        ) : items.length === 0 ? (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">
            No matching pricebook items
          </div>
        ) : (
          <ul className="max-h-56 overflow-auto">
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className="flex w-full items-center rounded px-2 py-1.5 text-left text-sm hover:bg-muted"
                  // mousedown so the pick lands before the input's blur closes the popover
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onChange(item.id);
                    setQuery('');
                    setOpen(false);
                  }}
                >
                  <span className="truncate">{item.name}</span>
                  {item.price ? (
                    <span className="ml-auto pl-2 font-mono text-xs text-muted-foreground">
                      {currency(item.price)}
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function RecurringServiceEditor({
  initial,
  saving,
  onCancel,
  onSave,
  coverageRules = [],
  lockedScope,
  context = 'plan',
}: {
  initial?: Partial<RecurringServiceConfig> | null;
  saving: boolean;
  onCancel: () => void;
  onSave: (input: PlanRecurringServiceInput) => void;
  coverageRules?: MembershipCoverageRule[];
  /** Customer-specific edits expose visit caps without changing plan authoring. */
  context?: 'plan' | 'membership';
  /**
   * Legacy/scoped callers may pin the visit to a property or coverage. The
   * membership-plan builder leaves this unset so authors choose the scope in
   * the unified Included visits list.
   */
  lockedScope?: { attachTo: 'PROPERTY' } | { attachTo: 'UNIT'; coverageId: string };
}) {
  const isNew = !initial;
  const lockDelivery = context === 'membership' && !isNew;
  const [d, setD] = useState<DraftState>(() => {
    const base = draftFrom(initial);
    if (!lockedScope) return base;
    return lockedScope.attachTo === 'UNIT'
      ? { ...base, attachTo: 'UNIT' as const, coverageId: lockedScope.coverageId }
      : { ...base, attachTo: 'PROPERTY' as const, coverageId: null };
  });
  const [tried, setTried] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [perUnitTime, setPerUnitTime] = useState(
    () => Number(draftFrom(initial).extraUnitMinutes) > 0,
  );
  const { data: jobTypes } = useJobTypes();
  const { data: businessUnits } = useBusinessUnits('active');
  const { data: equipmentCategories } = useEquipmentCategories();

  const set = <K extends keyof DraftState>(k: K, v: DraftState[K]) =>
    setD((prev) => ({ ...prev, [k]: v }));

  const jobErr = tried && !d.jobTypeId;
  const monthsErr = tried && d.recurrenceType === 'SEASONAL' && d.recurrenceMonths.length === 0;
  // An orphan row is the case where the id is present but no longer on the plan,
  // so presence is not the question — being on the plan is. `lockedScope` pins
  // its own coverage, and the card that pinned it is what proves it exists.
  const coverageOnPlan =
    d.attachTo !== 'UNIT' ||
    lockDelivery ||
    Boolean(lockedScope) ||
    coverageRules.some((coverage) => coverage.id === d.coverageId);
  const coverageErr = tried && !coverageOnPlan;
  const itemErr = tried && !d.invoiceTemplateItemId;
  const visitsErr = tried && d.durationType === 'FIXED' && !(Number(d.totalVisits) > 0);

  const summary = scheduleSummary({
    recurrenceType: d.recurrenceType,
    recurrenceInterval: Number(d.recurrenceInterval) || 1,
    recurrenceMonths: d.recurrenceMonths,
    durationType: d.durationType,
    totalVisits: Number(d.totalVisits) || null,
  });

  const baseMinutes = jobTypes?.find((jt) => jt.id === d.jobTypeId)?.defaultDuration ?? 0;
  /**
   * The service the visit delivers already declares its own work time, and that
   * is what one more covered unit really costs: the tech is already on site, so
   * the extra unit buys the work, not the drive. Offered, never applied behind
   * the author's back — only they know how much of it actually repeats.
   */
  const { data: pickedItem } = usePricebookItems(
    { ids: d.invoiceTemplateItemId ? [d.invoiceTemplateItemId] : [] },
    { enabled: Boolean(d.invoiceTemplateItemId), showErrorToast: false },
  );
  const itemWorkMinutes = Math.round((Number(pickedItem?.items?.[0]?.estimatedHours) || 0) * 60);
  const handleSave = () => {
    const seasonalNoMonths = d.recurrenceType === 'SEASONAL' && d.recurrenceMonths.length === 0;
    if (
      !d.jobTypeId ||
      !d.invoiceTemplateItemId ||
      seasonalNoMonths ||
      !coverageOnPlan ||
      (d.durationType === 'FIXED' && !(Number(d.totalVisits) > 0))
    ) {
      setTried(true);
      return;
    }
    const input = toInput(d);
    if (!input.label) {
      input.label = jobTypes?.find((jobType) => jobType.id === d.jobTypeId)?.name ?? null;
    }
    onSave(input);
  };

  const toggleMonth = (m: number) =>
    set(
      'recurrenceMonths',
      d.recurrenceMonths.includes(m)
        ? d.recurrenceMonths.filter((x) => x !== m)
        : [...d.recurrenceMonths, m].sort((a, b) => a - b),
    );

  const scopeName = !lockedScope
    ? null
    : lockedScope.attachTo === 'UNIT'
      ? (categoryName(
          equipmentCategories,
          coverageRules.find((coverage) => coverage.id === lockedScope.coverageId)
            ?.equipmentCategory,
        ) ?? 'this equipment')
      : 'the home';

  return (
    <div className="membership-plan-service-surface flex flex-col gap-3 p-4">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {isNew
            ? scopeName
              ? `New visit for ${scopeName}`
              : 'New recurring service'
            : `Editing · ${d.label || 'untitled'}`}
        </span>
        <Badge variant="secondary" className="font-mono text-[10px]">
          {isNew ? 'DRAFT' : 'INCLUDED VISIT'}
        </Badge>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
          {summary}
        </span>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <EditorRow
        label="What"
        hint={jobErr ? <span className="text-destructive">Pick a job type to continue</span> : null}
      >
        <Input
          aria-label="Recurring service customer label"
          value={d.label}
          onChange={(e) => set('label', e.target.value)}
          placeholder="Annual AC Tune-Up"
          className="w-full min-w-[180px] flex-1"
        />
        <Select value={d.jobTypeId} onValueChange={(v) => set('jobTypeId', v)}>
          <SelectTrigger
            aria-label="Recurring service job type"
            className={cn(
              'w-[220px] bg-card',
              jobErr && 'border-destructive ring-2 ring-destructive/20',
            )}
          >
            <SelectValue placeholder="Choose a job type" />
          </SelectTrigger>
          <SelectContent>
            {(jobTypes ?? []).map((jt) => (
              <SelectItem key={jt.id} value={jt.id}>
                {jt.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </EditorRow>

      {!lockedScope && !lockDelivery ? (
        <EditorRow label="Applies">
          <Pill
            active={d.attachTo === 'PROPERTY'}
            onClick={() =>
              setD((current) => ({
                ...current,
                attachTo: 'PROPERTY',
                coverageId: null,
                scaling: 'EXTEND',
                extraUnitMinutes: '0',
              }))
            }
          >
            Per home
          </Pill>
          <Pill
            active={d.attachTo === 'UNIT'}
            disabled={coverageRules.length === 0}
            onClick={() =>
              setD((current) => ({
                ...current,
                attachTo: 'UNIT',
                coverageId: coverageRules.some((coverage) => coverage.id === current.coverageId)
                  ? current.coverageId
                  : (coverageRules[0]?.id ?? null),
              }))
            }
          >
            Per covered unit
          </Pill>
        </EditorRow>
      ) : null}

      {!lockedScope && !lockDelivery && d.attachTo === 'UNIT' ? (
        <EditorRow
          label="Equipment"
          hint={
            coverageErr ? (
              <span className="text-destructive">Pick the coverage this visit belongs to</span>
            ) : d.coverageId && !coverageOnPlan ? (
              'The coverage this visit pointed at is gone — pick the one it belongs to now'
            ) : null
          }
        >
          <Select
            value={
              coverageRules.some((coverage) => coverage.id === d.coverageId)
                ? (d.coverageId ?? '')
                : ''
            }
            onValueChange={(value) => set('coverageId', value)}
          >
            <SelectTrigger
              aria-label="Recurring service coverage"
              className={cn(
                'w-[260px] bg-card',
                coverageErr && 'border-destructive ring-2 ring-destructive/20',
              )}
            >
              <SelectValue placeholder="Choose a coverage" />
            </SelectTrigger>
            <SelectContent>
              {coverageRules.map((coverage, index) => (
                <SelectItem key={coverage.id} value={coverage.id}>
                  {coverage.equipmentCategory || `Coverage ${index + 1}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </EditorRow>
      ) : null}

      <EditorRow label="How often">
        {RECURRENCE_TYPES.map((t) => (
          <Pill key={t} active={d.recurrenceType === t} onClick={() => set('recurrenceType', t)}>
            {t.charAt(0) + t.slice(1).toLowerCase()}
          </Pill>
        ))}
        {d.recurrenceType === 'SEASONAL' ? null : (
          <>
            <span className="ml-1 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
              Every
            </span>
            <Input
              aria-label="Recurrence interval"
              value={d.recurrenceInterval}
              onChange={(e) => set('recurrenceInterval', e.target.value.replace(/[^0-9]/g, ''))}
              className="h-9 w-16"
              inputMode="numeric"
            />
            <span className="text-sm text-muted-foreground">
              {unitWord(d.recurrenceType, Number(d.recurrenceInterval) || 1)}
            </span>
          </>
        )}
      </EditorRow>

      {d.recurrenceType === 'SEASONAL' ? (
        <EditorRow
          label="In"
          hint={
            monthsErr ? <span className="text-destructive">Pick at least one month</span> : null
          }
        >
          {MONTHS.map((label, i) => {
            const m = i + 1;
            return (
              <Pill key={m} active={d.recurrenceMonths.includes(m)} onClick={() => toggleMonth(m)}>
                {label}
              </Pill>
            );
          })}
        </EditorRow>
      ) : null}

      {context === 'membership' ? (
        <>
          <EditorRow label="Visit limit">
            <Pill
              active={d.durationType === 'CONTINUOUS'}
              onClick={() => set('durationType', 'CONTINUOUS')}
            >
              Ongoing
            </Pill>
            <Pill active={d.durationType === 'FIXED'} onClick={() => set('durationType', 'FIXED')}>
              Fixed number
            </Pill>
          </EditorRow>
          {d.durationType === 'FIXED' ? (
            <EditorRow
              label="Total visits"
              hint={
                visitsErr ? 'Enter at least one visit.' : 'Includes visits already used or booked.'
              }
            >
              <Input
                aria-label="Total included visits"
                inputMode="numeric"
                value={d.totalVisits}
                onChange={(event) => set('totalVisits', event.target.value.replace(/[^0-9]/g, ''))}
                className="w-24"
              />
            </EditorRow>
          ) : null}
          <EditorRow label="First visit">
            <Switch
              aria-label="First visit already completed"
              checked={d.firstVisitComplete}
              disabled={initial?.firstVisitComplete === true}
              onCheckedChange={(value) => set('firstVisitComplete', value)}
            />
            <span className="text-sm">Already completed</span>
          </EditorRow>
        </>
      ) : null}

      {d.attachTo === 'UNIT' && !lockDelivery ? (
        <EditorRow label="If several">
          <Select
            value={d.scaling}
            onValueChange={(value: 'EXTEND' | 'MULTIPLY') => {
              set('scaling', value);
              // One visit per unit has no "extra minutes" to add, so the toggle
              // that shows them goes with the number it was showing.
              if (value === 'MULTIPLY') {
                set('extraUnitMinutes', '0');
                setPerUnitTime(false);
              }
            }}
          >
            <SelectTrigger className="w-[220px] bg-card" aria-label="Recurring service scaling">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="EXTEND">One visit for all of them</SelectItem>
              {/* Each unit keeps its own due date and history. Whether two of them
                  go out on one trip is a booking choice, made in the queue. */}
              <SelectItem value="MULTIPLY">Track each unit separately</SelectItem>
            </SelectContent>
          </Select>
        </EditorRow>
      ) : null}

      {d.attachTo === 'UNIT' && d.scaling === 'EXTEND' ? (
        <EditorRow label="Length">
          <span className="text-sm">
            {baseMinutes ? `${baseMinutes} min from the job type` : 'Set by the job type'}
          </span>
          <span className="mx-1 h-5 w-px bg-border" />
          <Switch
            aria-label="Each unit after the first adds time"
            checked={perUnitTime}
            onCheckedChange={(on) => {
              setPerUnitTime(on);
              if (!on) set('extraUnitMinutes', '0');
            }}
          />
          <span className="text-sm text-muted-foreground">each unit after the first adds</span>
          {perUnitTime ? (
            <>
              <Input
                aria-label="Extra minutes per additional covered unit"
                value={d.extraUnitMinutes}
                inputMode="numeric"
                onChange={(event) =>
                  set('extraUnitMinutes', event.target.value.replace(/[^0-9]/g, ''))
                }
                className="h-9 w-16"
              />
              <span className="text-sm text-muted-foreground">min</span>
              {itemWorkMinutes > 0 && Number(d.extraUnitMinutes) !== itemWorkMinutes ? (
                <button
                  type="button"
                  className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
                  onClick={() => set('extraUnitMinutes', String(itemWorkMinutes))}
                >
                  use the service&apos;s {itemWorkMinutes} min
                </button>
              ) : null}
            </>
          ) : (
            <span className="text-sm text-muted-foreground">nothing</span>
          )}
        </EditorRow>
      ) : null}

      <EditorRow
        label="Delivers"
        hint={
          itemErr ? (
            <span className="text-destructive">
              Pick the service this visit delivers — without it the job carries no line
            </span>
          ) : null
        }
      >
        <div className="w-full max-w-[340px]">
          <InvoiceItemPicker
            value={d.invoiceTemplateItemId}
            onChange={(id) => set('invoiceTemplateItemId', id)}
            invalid={itemErr}
          />
        </div>
      </EditorRow>

      <button
        type="button"
        onClick={() => setShowMore((open) => !open)}
        className="w-fit font-mono text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground"
      >
        {showMore ? '▾' : '▸'} More options
      </button>

      {showMore ? (
        <div className="flex flex-col gap-3 rounded-lg bg-muted/50 p-3">
          <EditorRow label="Business unit">
            <Select
              value={d.businessUnitId ?? 'INHERIT'}
              onValueChange={(v) => set('businessUnitId', v === 'INHERIT' ? null : v)}
            >
              <SelectTrigger
                className="w-[220px] bg-card"
                aria-label="Recurring service business unit"
              >
                <SelectValue placeholder="Use job type default" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="INHERIT">Use job type default</SelectItem>
                {(businessUnits ?? []).map((bu) => (
                  <SelectItem key={bu.id} value={bu.id}>
                    {bu.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </EditorRow>

          <EditorRow label="Priority">
            <Select
              value={d.priority ?? 'INHERIT'}
              onValueChange={(v) => set('priority', v === 'INHERIT' ? null : v)}
            >
              <SelectTrigger className="w-[220px] bg-card" aria-label="Recurring service priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="INHERIT">Use job type default</SelectItem>
                {PRIORITIES.map((pr) => (
                  <SelectItem key={pr} value={pr}>
                    {pr.charAt(0) + pr.slice(1).toLowerCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </EditorRow>

          <EditorRow label="Tech note">
            <Input
              aria-label="Recurring service job summary"
              value={d.jobSummary}
              onChange={(e) => set('jobSummary', e.target.value)}
              placeholder="What the tech does on site"
              className="w-full max-w-[340px]"
            />
          </EditorRow>
        </div>
      ) : null}

      <div className="flex items-center gap-2 border-t border-border pt-3">
        {jobErr || monthsErr || itemErr || coverageErr || visitsErr ? (
          <span className="text-sm text-muted-foreground">
            Some fields need an answer before this can be saved.
          </span>
        ) : null}
        <span className="flex-1" />
        <Button variant="outline" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSave} disabled={saving}>
          {isNew ? 'Add visit' : 'Save visit'}
        </Button>
      </div>
    </div>
  );
}

/** One line of the editor: a mono label on the left, its controls on the right. */
function EditorRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="grid grid-cols-[86px_1fr] items-start gap-3">
      <span className="pt-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.1em] text-[#8a857c]">
        {label}
      </span>
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-2">{children}</div>
        {hint ? (
          <span className="font-mono text-[9px] uppercase tracking-wide text-[#8a857c]">
            {hint}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** `PACKAGED_AC_UNIT` is a storage key; people read "Packaged Air Conditioner". */
function categoryName(
  categories: Array<{ key: string; name: string }> | undefined,
  key: string | null | undefined,
): string | null {
  if (!key) return null;
  return categories?.find((category) => category.key === key)?.name ?? null;
}
